import axios from 'axios'
import { FileInfo } from '../../../types'
import { FilesService } from '../../files/FilesService'
import { OperationContext, OperationSpec } from './OperationContext'
import { RunnableOperation } from './RunnableOperation'

/**
 * Scarica un file dello spazio personale.
 *
 * Il download viene eseguito qui dentro e non dal chiamante: serve perché
 * l'operazione compaia in "Operazioni in corso" con progress reale e
 * possibilità di annullamento, invece di bloccare la UI in loading.
 *
 * Il blob risultante viene esposto su `blob`, insieme alle chiavi di cifratura
 * restituite dal backend: il chiamante li recupera nella callback
 * `operationComplete:<operationId>` e decide cosa farne (decifratura, anteprima
 * o salvataggio su disco, che restano responsabilità dell'app).
 */
export class RunnableFileDownload extends RunnableOperation {
  public blob?: Blob
  public encryptionDek?: string
  public encryptionIv?: string
  private abortController = new AbortController()

  constructor(userOpSpec: OperationSpec) {
    super(userOpSpec)
  }

  async defineRequest(context: OperationContext): Promise<void> {
    super.defineRequest(context)
    if (this.aborted) {
      throw new Error('Operation aborted')
    }

    context.declareRequestSpec = {
      operationType: 'file.download',
      pathName: this.userOpSpec.from,
    }

    if (this.userOpSpec.isEncryptedZone) {
      context.declareRequestSpec.encriptionIV = this.userOpSpec.iv
      context.declareRequestSpec.encriptionDEK = this.userOpSpec.enc_dek
    }
  }

  async execute(context: OperationContext): Promise<void> {
    if (this.aborted) {
      throw new Error('Operation aborted')
    }
    this.setStatus('running')

    const { download_url, encryption_dek, encryption_iv } =
      await FilesService.getDownloadRequest(this.userOpSpec.file as FileInfo)

    this.encryptionDek = encryption_dek
    this.encryptionIv = encryption_iv

    // Le chiavi arrivano solo ora: allineiamo la declare, che il chiamante non
    // puo' popolare prima di conoscerle.
    if (context.declareRequestSpec && this.userOpSpec.isEncryptedZone) {
      context.declareRequestSpec.encriptionDEK =
        encryption_dek || this.userOpSpec.enc_dek
      context.declareRequestSpec.encriptionIV =
        encryption_iv || this.userOpSpec.iv
    }

    const response = await axios.get(download_url, {
      responseType: 'blob',
      signal: this.abortController.signal,
      onDownloadProgress: (progressEvent) => {
        if (!progressEvent.total) {
          return
        }
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        )
        // setProgress(100) marca l'operazione come completata: l'ultimo step lo
        // lascia fare a processQueue, quando anche il blob e' disponibile.
        if (percentCompleted < 100) {
          this.setProgress(percentCompleted)
        }
      },
    })

    this.blob = response.data as Blob
  }

  abort(): void {
    this.aborted = true
    this.abortController.abort()
  }
}
